import React, { useMemo } from 'react'
import Box from '@mui/material/Box'
import Paper from '@mui/material/Paper'
import Typography from '@mui/material/Typography'
import {
  DndContext,
  MouseSensor,
  useSensor,
  useSensors,
  useDraggable,
  useDroppable,
} from '@dnd-kit/core'
import type { DragEndEvent } from '@dnd-kit/core'
import { groupRows, NO_VALUE_LABEL } from './groupRows'

export const MAX_KANBAN_COLUMNS = 12
export const NO_VALUE_COLUMN_KEY = '__no_value__'
export const OTHER_COLUMN_KEY = '__other__'

export interface KanbanColumn {
  key: string
  label: string
  value: unknown
  rows: Record<string, unknown>[]
  readOnly?: boolean
}

export interface KanbanViewProps {
  rows: Record<string, unknown>[]
  columnField: string
  titleField: string
  subtitleField?: string
  labelFor?: (value: unknown, sampleRow: Record<string, unknown>) => string
  onOpen: (rowId: string) => void
  onMove?: (rowId: string, newValue: unknown) => void
}

export function buildKanbanColumns(
  rows: Record<string, unknown>[],
  columnField: string,
  labelFor?: (value: unknown, sampleRow: Record<string, unknown>) => string,
): KanbanColumn[] {
  const groups = groupRows(rows, columnField, { labelFor })
  const columns: KanbanColumn[] = groups.map((g) => ({
    key: g.key === '' ? NO_VALUE_COLUMN_KEY : g.key,
    label: g.label,
    value: g.key === '' ? null : g.rows[0][columnField],
    rows: g.rows,
  }))
  if (columns.length <= MAX_KANBAN_COLUMNS) return columns
  const kept = columns.slice(0, MAX_KANBAN_COLUMNS - 1)
  const rest = columns.slice(MAX_KANBAN_COLUMNS - 1)
  kept.push({
    key: OTHER_COLUMN_KEY,
    label: 'Other',
    value: null,
    rows: rest.flatMap((c) => c.rows),
    readOnly: true,
  })
  return kept
}

export function resolveDragEnd(
  event: DragEndEvent,
  columns: KanbanColumn[],
): { rowId: string; newValue: unknown } | null {
  const overId = event.over?.id
  if (overId == null) return null
  const col = columns.find((c) => c.key === String(overId))
  if (!col || col.readOnly) return null
  return { rowId: String(event.active.id), newValue: col.value }
}

const KanbanCard: React.FC<{
  row: Record<string, unknown>
  titleField: string
  subtitleField?: string
  draggable: boolean
  onOpen: (rowId: string) => void
}> = ({ row, titleField, subtitleField, draggable, onOpen }) => {
  const id = String(row.id)
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id,
    disabled: !draggable,
  })
  return (
    <Paper
      ref={setNodeRef}
      variant="outlined"
      onClick={() => onOpen(id)}
      {...attributes}
      {...listeners}
      sx={{
        p: 1,
        cursor: 'pointer',
        opacity: isDragging ? 0.5 : 1,
        transform: transform ? `translate(${transform.x}px, ${transform.y}px)` : undefined,
        '&:hover': { borderColor: 'primary.main' },
      }}
    >
      <Typography sx={{ fontSize: 13, fontWeight: 500 }}>
        {String(row[titleField] ?? row.id ?? '')}
      </Typography>
      {subtitleField && row[subtitleField] != null && row[subtitleField] !== '' && (
        <Typography sx={{ fontSize: 12, color: 'text.secondary', mt: 0.25 }}>
          {String(row[subtitleField])}
        </Typography>
      )}
    </Paper>
  )
}

const KanbanColumnBox: React.FC<{
  column: KanbanColumn
  titleField: string
  subtitleField?: string
  droppable: boolean
  onOpen: (rowId: string) => void
}> = ({ column, titleField, subtitleField, droppable, onOpen }) => {
  const { setNodeRef, isOver } = useDroppable({
    id: column.key,
    disabled: !droppable || column.readOnly,
  })
  return (
    <Paper
      ref={setNodeRef}
      variant="outlined"
      sx={{
        width: 280,
        flexShrink: 0,
        display: 'flex',
        flexDirection: 'column',
        bgcolor: isOver ? 'action.hover' : 'background.default',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1.25, py: 1, borderBottom: 1, borderColor: 'divider' }}>
        <Typography sx={{ fontSize: 12, fontWeight: 600 }}>{column.label}</Typography>
        <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>{column.rows.length}</Typography>
      </Box>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, p: 1, overflowY: 'auto', flex: 1 }}>
        {column.rows.map((row) => (
          <KanbanCard
            key={String(row.id)}
            row={row}
            titleField={titleField}
            subtitleField={subtitleField}
            draggable={droppable && !column.readOnly}
            onOpen={onOpen}
          />
        ))}
      </Box>
    </Paper>
  )
}

export const KanbanView: React.FC<KanbanViewProps> = ({
  rows,
  columnField,
  titleField,
  subtitleField,
  labelFor,
  onOpen,
  onMove,
}) => {
  const columns = useMemo(() => buildKanbanColumns(rows, columnField, labelFor), [rows, columnField, labelFor])
  const sensors = useSensors(useSensor(MouseSensor, { activationConstraint: { distance: 8 } }))

  const handleDragEnd = (event: DragEndEvent) => {
    const res = resolveDragEnd(event, columns)
    if (res && onMove) onMove(res.rowId, res.newValue)
  }

  return (
    <DndContext sensors={sensors} onDragEnd={handleDragEnd}>
      <Box sx={{ display: 'flex', gap: 1.5, p: 1.5, overflowX: 'auto', alignItems: 'stretch', flex: 1, minHeight: 0 }}>
        {columns.map((col) => (
          <KanbanColumnBox
            key={col.key}
            column={col}
            titleField={titleField}
            subtitleField={subtitleField}
            droppable={!!onMove}
            onOpen={onOpen}
          />
        ))}
      </Box>
    </DndContext>
  )
}
